import { PersonAssignmentMeta, PersonRow, ScheduledHoursBlock, UserStatus } from '../../models';
import {
  EMPLOYEE_PHOTOS,
  EMPLOYEE_START_DATES,
  USERS,
} from '../prototype/company-directory-data';
import { PROTO_PERSONAS } from '../prototype/personas-data';

const personas = PROTO_PERSONAS as Record<string, any>;
const users = USERS as any[];
const startDates = EMPLOYEE_START_DATES as Record<string, string>;
const photos = EMPLOYEE_PHOTOS as Record<string, string>;

/**
 * A little seeded assignment data per persona so the self-service "My
 * Assignments" panel (Account) and the People directory show real content out of
 * the box — held certifications (with issue/expiry, one expiring soon), one being
 * worked toward, and a sample of equipment/software. Keyed by persona id;
 * everyone else starts empty. Dates are relative to the prototype's "today".
 */
interface AssignmentSeed {
  certifications?: string[];
  equipment?: string[];
  software?: string[];
  documents?: string[];
  assignmentMeta?: Record<string, PersonAssignmentMeta>;
}
const PERSONA_ASSIGNMENTS_SEED: Record<string, AssignmentSeed> = {
  // Joe is the acting (signed-in) user until auth lands — seed his profile richly.
  joe: {
    certifications: ['OSHA 30', 'First Aid / CPR', 'AGC CM-Lean'],
    equipment: ['Laptop (14")'],
    assignmentMeta: {
      'certifications:OSHA 30': { issuedDt: '2024-06-01', expiresDt: '2027-06-01', verified: true, certificateNumber: 'OSHA30-88214', issuingBody: 'OSHA' },
      // Expiring within ~2 months of the prototype's "today" — demos the flag.
      'certifications:First Aid / CPR': { issuedDt: '2024-10-30', expiresDt: '2026-10-30', verified: true, issuingBody: 'American Red Cross', certificateNumber: 'FA-2024-5567' },
      'certifications:AGC CM-Lean': { targetDt: '2026-12-31', reason: 'Required for the GC director transition.' },
      'equipment:Laptop (14")': { assignedDt: '2024-01-15', condition: 'EXCELLENT' },
    },
  },
  // A superintendent with a trade + one held cert and one still to earn.
  ben: {
    certifications: ['OSHA 30', 'First Aid / CPR'],
    assignmentMeta: {
      'certifications:OSHA 30': { issuedDt: '2025-02-15', expiresDt: '2028-02-15', verified: false, certificateNumber: 'OSHA30-90551', issuingBody: 'OSHA' },
      'certifications:First Aid / CPR': { targetDt: '2026-11-30', reason: 'Site safety lead requirement.' },
    },
  },
  // A Project Manager (pc-002): his position already requires OSHA 30 + Laptop/
  // Mobile + Procore/M365 — meta below marks those held/assigned, plus one
  // hand-added cert he's working toward.
  chris: {
    certifications: ['OSHA 30', 'AGC CM-Lean'],
    equipment: ['Laptop (14")'],
    software: ['Procore'],
    assignmentMeta: {
      'certifications:OSHA 30': { issuedDt: '2024-05-10', expiresDt: '2027-05-10', verified: true, certificateNumber: 'OSHA30-77120', issuingBody: 'OSHA' },
      'certifications:AGC CM-Lean': { targetDt: '2026-11-15', reason: 'PM career-pathway requirement.' },
      'equipment:Laptop (14")': { assignedDt: '2023-08-01', condition: 'GOOD', assetTag: 'DR-LT-014' },
      'software:Procore': { swStatus: 'ACTIVE', accessLevel: 'Project Manager', accountUsername: 'chardy', grantedDt: '2023-08-05' },
    },
  },
};

/** The prototype's `shifts` list as our scheduled-hours blocks (the prototype used the server's name). */
const asScheduledHours = (raw: any): ScheduledHoursBlock[] =>
  Array.isArray(raw)
    ? raw
        .filter((s) => s && (s.start || s.end || s.days))
        .map((s) => ({ start: s.start ?? '', end: s.end ?? '', days: Array.isArray(s.days) ? s.days : [] }))
    : [];

/**
 * People directory — one row per persona, joined with the user account (status,
 * hire date, photo) and the manager's display name. HR attribute fields that the
 * prototype stores as per-persona overrides start empty (seeded personas have
 * none); they become editable in-session via the add/edit form.
 */
export const PEOPLE_SEED: readonly PersonRow[] = Object.values(personas).map((p): PersonRow => {
  const user = users.find((u) => u.personaId === p.id);
  const manager = p.reportsTo ? personas[p.reportsTo]?.name ?? p.reportsTo : p.isCEO ? 'Chief Executive' : '';
  const status: UserStatus = user?.status ?? (p.inOnboarding ? 'pending-onboarding' : 'active');
  const seeded = PERSONA_ASSIGNMENTS_SEED[p.id] ?? {};
  return {
    id: p.id,
    name: p.name ?? '',
    firstName: p.firstName ?? '',
    middleName: p.middleName ?? '',
    lastName: p.lastName ?? '',
    initials: p.initials ?? '',
    title: p.title ?? '',
    function: p.function ?? '',
    tier: p.tier ?? '',
    growth: p.growth ?? '',
    division: p.opco ?? '',
    employmentType: '',
    payClass: 'Hourly — Non-exempt',
    hourlyWage: p.hourlyWage ?? null,
    location: '',
    reportsToName: manager,
    reportsToCode: p.reportsTo ?? '',
    // The seed's three booleans, as the list the server answers with.
    roles: [
      ...(p.isAdmin ? ['admin'] : []),
      ...(p.isCEO ? ['ceo'] : []),
      ...(p.isSupervisor ? ['supervisor'] : []),
      ...((p.trades ?? []).length ? ['trade'] : ['employee']),
    ],
    startDate: user ? startDates[user.id] ?? null : null,
    status,
    photo: user ? photos[user.id] ?? '' : '',
    trades: Array.isArray(p.trades) ? p.trades : [],
    scheduledHours: asScheduledHours(p.shifts),
    documents: seeded.documents ?? [],
    certifications: seeded.certifications ?? [],
    equipment: seeded.equipment ?? [],
    software: seeded.software ?? [],
    assignmentMeta: seeded.assignmentMeta,
    added: false,
  };
});
